import { useQueryClient } from "@tanstack/react-query";

import { useInternationalization, useNotification } from "@/lib/contexts";
import { CustomAlertSchedule } from "@/pages/rooms/details/components/settings/components/alert-schedules/alert-schedules.form";
import { UpdateRoomSettingsAlarmSchedulesApiDto } from "@/services/API/rooms/update-room-custom-alarms.api.services";
import API from "../../..";
import { UpdateRoomErpIntegrationsError } from "../../../rooms/errors";
import { useMutationWrapper } from "../../wrappers";
import { roomDetailsKey, updateRoomCustomAlarmsKey } from "../room-query-keys";

export interface UpdateRoomSettingsAlarmsSchedulesMutationParams {
  roomId: string;
  isCustomerOrManagerUser: boolean;
}

export interface UpdateRoomSettingsAlarmsSchedulesDto {
  _id: string;
  alertSchedules: CustomAlertSchedule[];
}

export function useMutationUpdateRoomSettingsAlarmsSchedules({
  roomId,
  isCustomerOrManagerUser,
}: UpdateRoomSettingsAlarmsSchedulesMutationParams) {
  const { t } = useInternationalization("pages.rooms.alerts.update-room-settings-alarms");
  const { success, error } = useNotification();

  const queryClient = useQueryClient();

  const invalidateQueryKeys = () => {
    queryClient.invalidateQueries({ queryKey: roomDetailsKey(roomId) });
  };

  const onError = async (err: Error) => {
    const isErpIntegrationsError = err instanceof UpdateRoomErpIntegrationsError;
    if (!isCustomerOrManagerUser) {
      const errorMessage = isErpIntegrationsError ? t("erp-integrations-error") : t("error");
      error(errorMessage);
    } else {
      !isErpIntegrationsError && error(t("error"));
      isErpIntegrationsError && success(t("success"));
    }

    isErpIntegrationsError && invalidateQueryKeys();
  };

  const onSuccess = async () => {
    success(t("success"));
    invalidateQueryKeys();
  };

  const { mutate, isPending, isSuccess } = useMutationWrapper<
    boolean,
    UpdateRoomSettingsAlarmSchedulesApiDto
  >({
    mutationKey: updateRoomCustomAlarmsKey(roomId),
    mutationFn: API.Rooms.updateRoomSettingsAlarmSchedules,
    onSuccess,
    onError,
  });

  return {
    mutate,
    isPending,
    isSuccess,
  };
}
